import { EntityAudit } from "@modernary/mui/components/audit";
import {
  EntityDocumentForm,
  EntityDocumentMultipleForm,
  EntityDocuments,
  EntityDocumentsChildList,
} from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { RouterView, type RouterOptions } from "vue-router";

// Troubleshoot your routes using this - https://paths.esm.dev

const names = {
  constructionProjects: "ConstructionProject.ConstructionProjects",
  list: "ConstructionProject.ConstructionProjectList",
  new: "ConstructionProject.ConstructionProjectNew",
  base: "ConstructionProject.ConstructionProjectBase",
  view: "ConstructionProject.ConstructionProjectView",
  edit: "ConstructionProject.ConstructionProjectEdit",
  complaints: "ConstructionProject.ConstructionProjectComplaints",
  applications: "ConstructionProject.ConstructionProjectApplications",
  permits: "ConstructionProject.ConstructionProjectPermits",
  users: "ConstructionProject.ConstructionProjectUsers",
  userNew: "ConstructionProject.ConstructionProjectUserNew",
  complaintInvestigationsChildList: "ConstructionProject.ComplaintInvestigationsChildList",
  dustInspectionsChildList: "ConstructionProject.DustInspectionsChildList",
  enforcementActions: "ConstructionProject.ConstructionProjectEnforcementActions",
  documents: "ConstructionProject.ConstructionProjectDocuments",
  documentsEdit: "ConstructionProject.ConstructionProjectDocumentsEdit",
  documentsNew: "ConstructionProject.ConstructionProjectDocumentsNew",
  notes: "ConstructionProject.ConstructionProjectNotes",
  noteEdit: "ConstructionProject.ConstructionProjectNoteEdit",
  noteNew: "ConstructionProject.ConstructionProjectNoteNew",
  audit: "ConstructionProject.ConstructionProjectAudit",
};

export { names as constructionProjectRouteNames };

const constructionProjectRoutes: RouterOptions["routes"] = [
  {
    path: "/constructionProject",
    name: names.constructionProjects,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: { title: "Construction Projects" },
        component: () => import("@/views/constructionProject/ConstructionProjectList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/constructionProject/ConstructionProjectBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "New Construction Project", isNew: true },
            component: () => import("@/views/constructionProject/ConstructionProjectEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/constructionProject/ConstructionProjectBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Construction Project View" },
            component: () => import("@/views/constructionProject/ConstructionProjectView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Construction Project Edit", isForm: true },
            component: () => import("@/views/constructionProject/ConstructionProjectEdit.vue"),
          },
          {
            path: "complaints",
            name: names.complaints,
            meta: { title: "Construction Project Complaints" },
            component: () => import("@/views/constructionProject/ComplaintChildList.vue"),
          },
          {
            path: "applications",
            name: names.applications,
            meta: { title: "Construction Project Applications" },
            component: () => import("@/views/constructionProject/ApplicationChildList.vue"),
          },
          {
            path: "permits",
            name: names.permits,
            meta: { title: "Construction Project Permits" },
            component: () => import("@/views/constructionProject/PermitChildList.vue"),
          },
          {
            path: "investigations",
            name: names.complaintInvestigationsChildList,
            meta: { title: "Investigations" },
            component: () => import("@/views/constructionProject/InvestigationChildList.vue"),
          },
          {
            path: "inspections",
            name: names.dustInspectionsChildList,
            meta: { title: "Inspections" },
            component: () => import("@/views/constructionProject/DustInspectionChildList.vue"),
          },
          {
            path: "enforcementActions",
            name: names.enforcementActions,
            meta: { title: "Construction Project Enforcement Actions" },
            component: () => import("@/views/constructionProject/EnforcementActionChildList.vue"),
          },
          {
            path: "users",
            name: names.users,
            meta: { title: "Construction Project Users" },
            component: () => import("@/views/constructionProject/ConstructionProjectUsers.vue"),
          },
          {
            path: "users/new",
            name: names.userNew,
            meta: { title: "Add Construction Project User", isForm: true },
            component: () => import("@/views/constructionProject/ConstructionProjectUser.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Construction Project Documents" },
            component: {
              render: () => <EntityDocumentsChildList resourceTypeKey="constructionProject" />,
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentsEdit,
            meta: { title: "Edit Construction Project Document", isForm: true },
            component: {
              render: () => <EntityDocumentForm resourceTypeKey="constructionProject" />,
            },
          },
          {
            path: "documents/new",
            name: names.documentsNew,
            meta: { title: "Create Construction Project Documents", isForm: true },
            component: {
              render: () => <EntityDocumentMultipleForm resourceTypeKey="constructionProject" />,
            },
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Construction Project Notes" },
            component: {
              render: () => (
                <EntityNotes
                  idField="constructionProjectId"
                  routeNamePrefix="ConstructionProject.ConstructionProjectNote"
                />
              ),
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Edit Construction Project Note", isForm: true },
            component: () => import("@/views/constructionProject/ConstructionProjectNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Create Construction Project Note", isForm: true },
            component: () => import("@/views/constructionProject/ConstructionProjectNote.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Construction Project Audit Activity" },
            component: {
              render: () => <EntityAudit entityType="ConstructionProject" />,
            },
          },
        ],
      },
    ],
  },
];
export default constructionProjectRoutes;
